/* eslint-disable no-underscore-dangle */
/**
 * uPlot Canvas Renderer - Core rendering engine for line visualizations in canvas mode
 *
 * This file bridges Splunk visualization data with the uPlot charting library for high-performance
 * canvas-based line chart rendering. Critical for canvas mode line charts and trellis layouts.
 *
 * How it works with uPlot:
 * • Transforms Splunk DataSource into uPlot AlignedData format (array of columns)
 * • Maps Splunk series/scale configs to uPlot options (series, scales, axes, cursor settings)
 * • Registers custom hooks (drawAxes, draw, destroy) to extend uPlot's rendering with categorical labels and overlay alignment
 * • Provides PlotUtilities wrapper exposing zoom, pan, cursor, and coordinate conversion methods
 * • Manages uPlot lifecycle through factory pattern, maintaining stable reference across re-renders
 * • Optimizes performance with memoization and devicePixelRatio-aware font scaling for high-DPI displays
 *
 * Testing Strategy:
 * ⚠️  Unit tests for this file require extensive mocking of uPlot internals and provide limited value
 *     since actual rendering behavior can only be verified through real canvas charts.
 *
 * When making changes to this file:
 * 1. Extract pure functions/logic to uplot-utils.ts where they can be properly unit tested
 * 2. Add Loki visual regression tests to verify rendering output (snapshots)
 * 3. Add Cypress e2e tests with interaction testing for:
 *    - Zoom and pan functionality
 *    - Cursor/tooltip positioning
 *    - Series focus and hover behavior
 *    - Categorical label rendering
 *    - High-DPI display handling
 *
 * See: packages/visualization-canvas/src/renderers/tests/uplot-utils.test.ts
 *      packages/integration-tests/src/e2e-visual/canvas-line/
 */
import isEqual from 'lodash/isEqual';
import isFunction from 'lodash/isFunction';
import some from 'lodash/some';
import React, { useMemo, useState } from 'react';
import uPlot from 'uplot';
import UplotReact from 'uplot-react';

import { useMemoGuaranteed } from '../utilities/useMemoGuaranteed';
import { getFormattedDataColumns } from '../getFormattedDataColumns';
import { exists, getXAxisResolution, isNumber, createCanvasScaleHooks } from '../utilities/utils';
import {
    OnFocusSeriesFn,
    OnSetCursorFn,
    PlotRendererFactory,
    PlotUtilities,
    RenderProps,
    PlotUtilitiesFn,
} from './types';
import {
    isTimeSeriesData,
    mapDSToScales,
    mapDSToSeries,
    mapScalesToAxes,
    SeriesWithKey,
    stackData,
    drawHoverMarker,
    HOVER_MARKER_OUTER_RADIUS_PX,
    parseAndScaleFont,
    getRotatedLabels,
    CustomRotatedLabel,
    drawMinorGridLines,
    drawMajorTicks,
    drawMinorTicks,
    calculateExpandedPadding,
} from './uplot-utils';
import { useScaleFactor } from '../utilities/useScaleFactor';
import { useDevicePixelRatio } from '../utilities/useDevicePixelRatio';
import { createDataLabelsPlugin } from '../DataLabelsPlugin';

import 'uplot/dist/uPlot.min.css';

/** Default maximum number of series that still allows for "closest series focus" functionality to be enabled. */
const FOCUS_MAX_SERIES = 30;
const INACTIVE_SERIES_ALPHA = 0.2;

/**
 * Checks if a value is valid for cursor point rendering (not null/undefined/NaN)
 */
const isValidDataValue = (val: number | null | undefined): boolean =>
    val !== null && val !== undefined && !(typeof val === 'number' && Number.isNaN(val));

/**
 * Gets the color for a series, handling both static and dynamic color functions
 */
const getSeriesColor = (stroke: uPlot.Series.Stroke | undefined, u: uPlot, seriesIdx: number): string => {
    if (typeof stroke === 'function') {
        return stroke(u, seriesIdx) as string;
    }
    return stroke as string;
};

/**
 * Draws cursor points for all series (SeriesCompare mode)
 */
const drawAllSeriesPoints = (
    u: uPlot,
    ctx: CanvasRenderingContext2D,
    idx: number,
    pointPadding: number
): void => {
    for (let i = 1; i < u.series.length; i += 1) {
        const s = u.series[i];
        if (s?.show && u.data[i] && idx < u.data[i].length) {
            const xVal = u.data[0][idx];
            const yVal = u.data[i][idx];

            if (isValidDataValue(xVal) && isValidDataValue(yVal)) {
                const xPos = u.valToPos(xVal, 'x', false) + pointPadding;
                const yPos = u.valToPos(yVal, s.scale || 'y', false) + pointPadding;
                const color = getSeriesColor(s.stroke, u, i);

                drawHoverMarker(ctx, xPos, yPos, color);
            }
        }
    }
};

/**
 * Finds the closest series to the cursor position and draws its point (Standard mode)
 * @param sf - Scale factor used for cursor coordinates (cursor position is divided by this)
 */
const drawClosestSeriesPoint = (
    u: uPlot,
    ctx: CanvasRenderingContext2D,
    idx: number,
    cursorTop: number,
    pointPadding: number
): void => {
    let closestSeriesIdx = -1;
    let closestDistance = Infinity;

    // cursor.top is already in CSS pixels from cursorPlugin (clientY - rect.top) / scaleFactor
    // valToPos also returns CSS pixels, so we can compare directly
    const cursorTopCss = cursorTop;

    // Find the closest series
    for (let i = 1; i < u.series.length; i += 1) {
        const s = u.series[i];
        if (s?.show && u.data[i] && idx < u.data[i].length) {
            const yVal = u.data[i][idx];
            if (isValidDataValue(yVal)) {
                const yPos = u.valToPos(yVal, s.scale || 'y', false);
                const distance = Math.abs(cursorTopCss - yPos);

                if (distance < closestDistance) {
                    closestDistance = distance;
                    closestSeriesIdx = i;
                }
            }
        }
    }

    if (closestSeriesIdx === -1) {
        return;
    }

    // Draw the point for the closest series
    const s = u.series[closestSeriesIdx];
    const xVal = u.data[0][idx];
    const yVal = u.data[closestSeriesIdx][idx];

    const xPos = u.valToPos(xVal, 'x', false) + pointPadding;
    const yPos = u.valToPos(yVal, s.scale || 'y', false) + pointPadding;
    const color = getSeriesColor(s.stroke, u, closestSeriesIdx);

    drawHoverMarker(ctx, xPos, yPos, color);
};

/**
 * Configuration for drawing axis features (grids, ticks)
 */
interface AxisDrawConfig {
    showYMinorGridLines?: boolean;
    showY2MinorGridLines?: boolean;
    showYMajorGridLines?: boolean;
    showY2MajorGridLines?: boolean;
    showYMajorTicks?: boolean;
    showY2MajorTicks?: boolean;
    showYMinorTicks?: boolean;
    showY2MinorTicks?: boolean;
    yAxisMajorTickSize?: number;
    y2AxisMajorTickSize?: number;
    yAxisMinorTickSize?: number;
    y2AxisMinorTickSize?: number;
    gridStroke: string;
}

/**
 * Draws all axis features (grids, ticks) based on configuration
 */
const drawAxisFeatures = (u: uPlot, config: AxisDrawConfig): void => {
    // Draw minor grid lines
    if (config.showYMinorGridLines) {
        drawMinorGridLines(u, 'y', config.gridStroke, config.showYMajorGridLines ?? true);
    }
    if (config.showY2MinorGridLines) {
        drawMinorGridLines(u, 'y2', config.gridStroke, config.showY2MajorGridLines ?? false);
    }

    // Draw major ticks
    if (config.showYMajorTicks) {
        drawMajorTicks(u, 'y', config.gridStroke, config.yAxisMajorTickSize ?? 6, 'left');
    }
    if (config.showY2MajorTicks) {
        drawMajorTicks(u, 'y2', config.gridStroke, config.y2AxisMajorTickSize ?? 6, 'right');
    }

    // Draw minor ticks
    if (config.showYMinorTicks) {
        drawMinorTicks(u, 'y', config.gridStroke, config.yAxisMinorTickSize ?? 4, 'left');
    }
    if (config.showY2MinorTicks) {
        drawMinorTicks(u, 'y2', config.gridStroke, config.y2AxisMinorTickSize ?? 4, 'right');
    }
};

/**
 * Draws the bottom axis line
 */
const drawBottomAxisLine = (
    ctx: CanvasRenderingContext2D,
    bbox: { left: number; top: number; width: number; height: number },
    strokeColor: string
): void => {
    const xLft = bbox.left;
    const xRgt = bbox.left + bbox.width;
    const yBtm = bbox.top + bbox.height;

    ctx.save();
    ctx.beginPath();
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = 2;
    ctx.moveTo(xLft, yBtm);
    ctx.lineTo(xRgt, yBtm);
    ctx.stroke();
    ctx.restore();
};

/**
 * Draws the left axis line
 */
const drawLeftAxisLine = (
    ctx: CanvasRenderingContext2D,
    bbox: { left: number; top: number; width: number; height: number },
    strokeColor: string
): void => {
    const xLft = bbox.left;
    const yTop = bbox.top;
    const yBtm = bbox.top + bbox.height;

    ctx.save();
    ctx.beginPath();
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = 2;
    ctx.moveTo(xLft, yTop);
    ctx.lineTo(xLft, yBtm);
    ctx.stroke();
    ctx.restore();
};

/**
 * Draws the right axis line
 */
const drawRightAxisLine = (
    ctx: CanvasRenderingContext2D,
    bbox: { left: number; top: number; width: number; height: number },
    strokeColor: string
): void => {
    const xRgt = bbox.left + bbox.width;
    const yTop = bbox.top;
    const yBtm = bbox.top + bbox.height;

    ctx.save();
    ctx.beginPath();
    ctx.strokeStyle = strokeColor;
    ctx.lineWidth = 2;
    ctx.moveTo(xRgt, yTop);
    ctx.lineTo(xRgt, yBtm);
    ctx.stroke();
    ctx.restore();
};

/** Renders custom multi-line labels for rotated expanded timestamps */
const renderRotatedExpandedLabels = (
    u: uPlot,
    rotatedLabels: CustomRotatedLabel[],
    labelRotation: number,
    fillStyle: string,
    font: string
): void => {
    if (rotatedLabels.length === 0) return;

    const { ctx, bbox } = u;
    const yBtm = bbox.top + bbox.height;
    const dpr = window.devicePixelRatio || 1;
    const lineHeight = 16 * dpr;
    const absRotation = Math.abs(labelRotation);
    const tickGap = (absRotation === 90 ? 12 : 6) * dpr;
    const angleRad = (labelRotation * Math.PI) / 180;

    ctx.save();
    ctx.font = font;
    ctx.fillStyle = fillStyle;

    rotatedLabels.forEach(({ split, label }) => {
        if (!label) return;

        const xPos = Math.round(u.valToPos(split, 'x', true));
        const yStart = Math.round(yBtm + tickGap);
        const lines = label.split('\n');

        lines.forEach((line, idx) => {
            ctx.save();
            const perpX = -Math.sin(angleRad) * idx * lineHeight;
            const perpY = Math.cos(angleRad) * idx * lineHeight;
            ctx.translate(xPos + perpX, yStart + perpY);
            ctx.rotate(angleRad);
            ctx.textAlign = labelRotation > 0 ? 'left' : 'right';
            ctx.textBaseline = 'top';
            ctx.fillText(line, 0, 0);
            ctx.restore();
        });
    });

    ctx.restore();
};

/**
 * Truncates text from the middle with ellipsis based on pixel width.
 */
const truncateMiddleLabel = (text: string, maxWidth: number, ctx: CanvasRenderingContext2D): string => {
    if (!text || maxWidth <= 0) return text || '';

    const fullWidth = ctx.measureText(text).width;
    if (fullWidth <= maxWidth) return text;

    const ellipsis = '…';
    const ellipsisWidth = ctx.measureText(ellipsis).width;
    const availableWidth = maxWidth - ellipsisWidth;

    if (availableWidth <= 0) return ellipsis;

    // Binary search for the optimal split point
    let startLen = Math.floor(text.length / 2);
    let endLen = Math.floor(text.length / 2);

    while (startLen > 0 || endLen > 0) {
        const startPart = text.slice(0, startLen);
        const endPart = text.slice(-endLen);
        const totalWidth = ctx.measureText(startPart).width + ctx.measureText(endPart).width;

        if (totalWidth <= availableWidth) {
            return startPart + ellipsis + endPart;
        }

        if (startLen >= endLen && startLen > 0) {
            startLen -= 1;
        } else if (endLen > 0) {
            endLen -= 1;
        }
    }

    return ellipsis;
};

/**
 * Renders categorical x-axis labels with optional rotation and truncation
 */
const renderCategoricalLabels = (
    u: uPlot,
    rawXaxisValues: (string | number)[],
    labelRotation: number,
    scaleFactor: number,
    fillStyle: string,
    font: string
): void => {
    const { ctx, bbox } = u;
    const yBtm = bbox.top + bbox.height;
    const plotWidth = bbox.width;

    ctx.save();
    ctx.fillStyle = fillStyle;
    ctx.font = font;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'top';

    // Calculate tick spacing for truncation
    const numLabels = rawXaxisValues.length;
    const tickSpacing = numLabels > 1 ? plotWidth / numLabels : plotWidth;
    // Use 90% of tick spacing to prevent overlap (labels are centered on ticks)
    const maxLabelWidth = tickSpacing * 0.9;

    for (let i = 0; i < rawXaxisValues.length; i += 1) {
        const rawLabel = rawXaxisValues[i]?.toString();
        if (rawLabel) {
            // Truncate label with middle ellipsis if too long
            const label = truncateMiddleLabel(rawLabel, maxLabelWidth, ctx);
            const xPos = u.valToPos(i + 0.5, 'x', true);

            if (labelRotation !== 0) {
                const yPos = yBtm + 18 * scaleFactor;
                ctx.save();
                ctx.translate(xPos, yPos);
                ctx.rotate((labelRotation * Math.PI) / 180);
                ctx.textAlign = labelRotation > 0 ? 'left' : 'right';
                ctx.textBaseline = 'middle';
                ctx.fillText(label, 0, 0);
                ctx.restore();
            } else {
                const yPos = yBtm + 12 * scaleFactor;
                ctx.fillText(label, xPos, yPos);
            }
        }
    }

    ctx.restore();
};

type UPlotRenderComponentProps = RenderProps & {
    onReady: (u: uPlot) => void;
    onSetCursor: (u: uPlot) => void;
    onSetSeries: (u: uPlot, seriesIndex: number | null) => void;
    onSetScale: (u: uPlot, scaleKey: string) => void;
    // SCP-80914: fired after every uPlot setSize so annotation portals can
    // re-evaluate valToPos with up-to-date x-scale coordinates.
    onSetSize?: () => void;
};

const UPlotRenderComponent: React.FC<UPlotRenderComponentProps> = props => {
    // Data, series, scales, and axes conversion
    const locale = props.userLocale;
    const { rawXaxisValues, timestampFormat } = props;
    const isTimeSeries = isTimeSeriesData(props.data);
    const { fields, columns: dataColumns } = props.data;
    const dataLength = dataColumns[0]?.length ?? 0;

    // For categorical data with multiple points, shift x-values by 0.5 to center them between ticks
    const xColumn =
        !isTimeSeries && dataColumns[0] && dataLength > 1
            ? dataColumns[0].map((_val, idx) => idx + 0.5)
            : dataColumns[0];

    const columns = [xColumn, ...getFormattedDataColumns(props.data, props.seriesConfig, props.scaleConfig)];

    const xAxisResolution = useMemo(() => getXAxisResolution(props.data), [props.data]);
    const series = useMemo(
        () =>
            mapDSToSeries({
                fields,
                hiddenSeries: props.hiddenSeries,
                seriesColors: props.seriesColors,
                seriesColorsByField: props.seriesColorsByField,
                markerFill: props.theme.markerFill,
                dataLength,
                seriesConfig: props.seriesConfig,
                scaleConfig: props.scaleConfig,
                showLineSmoothing: props.showLineSmoothing,
                stepPosition: props.stepPosition,
                areaOpacity: props.areaOpacity,
            }),
        [
            fields,
            props.scaleConfig,
            props.seriesConfig,
            props.hiddenSeries,
            props.seriesColors,
            props.seriesColorsByField,
            props.theme.markerFill,
            dataLength,
            props.showLineSmoothing,
            props.stepPosition,
            props.areaOpacity,
        ]
    );
    const shouldLimitHoverDistance = useMemo(
        () => some(props.seriesConfig, v => exists(v.type) && v.type === 'stacked-bar'),
        [props.seriesConfig]
    );

    const scales = useMemo(
        () =>
            mapDSToScales({
                seriesConfig: props.seriesConfig,
                scaleConfig: props.scaleConfig,
                range: props.range,
                isTimeSeries,
                yAxisMajorTickInterval: props.yAxisMajorTickInterval,
                y2AxisMajorTickInterval: props.y2AxisMajorTickInterval,
                showYAxisWithZero: props.showYAxisWithZero,
                showY2AxisWithZero: props.showY2AxisWithZero,
            }),
        [
            props.range,
            props.scaleConfig,
            props.seriesConfig,
            isTimeSeries,
            props.yAxisMajorTickInterval,
            props.y2AxisMajorTickInterval,
            props.showYAxisWithZero,
            props.showY2AxisWithZero,
        ]
    );

    // Detect if we have Y2-only scenario (for proper axis configuration)
    const isY2Only = useMemo(() => {
        let hasY2Config = false;
        let hasYAxisWithData = false;

        // Check for Y2 config
        if (props.scaleConfig) {
            Object.keys(props.scaleConfig).forEach(key => {
                if (key !== 'x' && props.scaleConfig?.[key]) {
                    const config = props.scaleConfig[key];
                    if (key === 'y2' || config.side === 'right') {
                        hasY2Config = true;
                    }
                }
            });
        }

        // Check if Y axis has data
        if (props.seriesConfig) {
            Object.values(props.seriesConfig).forEach(seriesConfig => {
                // Check both top-level scale and overrides.scale
                const scaleValue = seriesConfig?.scale || seriesConfig?.overrides?.scale;
                if (scaleValue === 'y' || (!scaleValue && !hasY2Config)) {
                    hasYAxisWithData = true;
                }
            });
        }

        return hasY2Config && !hasYAxisWithData;
    }, [props.scaleConfig, props.seriesConfig]);

    const axes = useMemo(
        () =>
            mapScalesToAxes({
                xAxisResolution,
                xAxisMaxLabelParts: props.xAxisMaxLabelParts,
                theme: props.theme,
                locale,
                hideXAxis: props.hideXAxis,
                range: props.range,
                isTimeSeries,
                rawXaxisValues,
                scaleConfig: props.scaleConfig,
                timestampFormat,
                dataLength,
                showXMajorGridLines: props.showXMajorGridLines,
                showYMajorGridLines: props.showYMajorGridLines,
                showY2MajorGridLines: props.showY2MajorGridLines,
                showXMajorTicks: props.showXMajorTicks,
                xAxisMajorTickSize: props.xAxisMajorTickSize,
                yAxisMajorTickInterval: props.yAxisMajorTickInterval,
                y2AxisMajorTickInterval: props.y2AxisMajorTickInterval,
                isY2Only,
            }),
        [
            xAxisResolution,
            props.theme,
            props.scaleConfig,
            props.hideXAxis,
            locale,
            props.range,
            isTimeSeries,
            rawXaxisValues,
            timestampFormat,
            dataLength,
            props.showXMajorGridLines,
            props.showYMajorGridLines,
            props.showY2MajorGridLines,
            props.showXMajorTicks,
            props.xAxisMajorTickSize,
            props.yAxisMajorTickInterval,
            props.y2AxisMajorTickInterval,
            props.xAxisMaxLabelParts,
            isY2Only,
        ]
    );

    // Perform stacking adjustments
    const { data: alignedData, bands } = useMemoGuaranteed<ReturnType<typeof stackData>>(
        previous => {
            const output = stackData(series, columns as uPlot.AlignedData);

            if (isEqual(output.bands, previous?.bands)) {
                return { data: output.data, bands: previous?.bands };
            }

            return output;
        },
        [columns, series]
    );

    const maxSeriesFocus = props.maxSeriesFocus ?? FOCUS_MAX_SERIES;

    const scaleFactor = useScaleFactor();
    const { version: dprVersion } = useDevicePixelRatio();

    // Memoized uPlot options
    const opts: uPlot.Options = useMemo(() => {
        let destroyed = false;

        const basePlugins: uPlot.Plugin[] = [
            {
                hooks: {
                    ...createCanvasScaleHooks(scaleFactor, props.onReady),
                    setCursor: props.onSetCursor,
                    setSeries: props.onSetSeries,
                    setScale: props.onSetScale,
                    drawAxes: (u): void => {
                        if (destroyed) {
                            return;
                        }

                        // Draw axis features (grids and ticks)
                        drawAxisFeatures(u, {
                            showYMinorGridLines: props.showYMinorGridLines,
                            showY2MinorGridLines: props.showY2MinorGridLines,
                            showYMajorGridLines: props.showYMajorGridLines,
                            showY2MajorGridLines: props.showY2MajorGridLines,
                            showYMajorTicks: props.showYMajorTicks,
                            showY2MajorTicks: props.showY2MajorTicks,
                            showYMinorTicks: props.showYMinorTicks,
                            showY2MinorTicks: props.showY2MinorTicks,
                            yAxisMajorTickSize: props.yAxisMajorTickSize,
                            y2AxisMajorTickSize: props.y2AxisMajorTickSize,
                            yAxisMinorTickSize: props.yAxisMinorTickSize,
                            y2AxisMinorTickSize: props.y2AxisMinorTickSize,
                            gridStroke: props.theme.axis.gridStroke,
                        });

                        // Draw axis lines conditionally
                        // showXAxisLine defaults to true for backwards compatibility
                        // Also show X-axis line when Y or Y2 major grid lines are enabled to avoid visual gap
                        if (
                            props.showXAxisLine !== false ||
                            props.showYMajorGridLines ||
                            props.showY2MajorGridLines
                        ) {
                            drawBottomAxisLine(u.ctx, u.bbox, props.theme.axis.axisStroke);
                        }
                        if (props.showYAxisLine) {
                            drawLeftAxisLine(u.ctx, u.bbox, props.theme.axis.axisStroke);
                        }
                        if (props.showY2AxisLine) {
                            drawRightAxisLine(u.ctx, u.bbox, props.theme.axis.axisStroke);
                        }

                        // Render categorical labels for non-time series data
                        const labelRotation = props.scaleConfig?.x?.labelRotation ?? 0;
                        if (!isTimeSeries && dataLength > 1 && rawXaxisValues?.length > 0) {
                            const pxRatio = window.devicePixelRatio || 1;
                            const scaledFont = parseAndScaleFont(props.theme.font, pxRatio);
                            renderCategoricalLabels(
                                u,
                                rawXaxisValues,
                                labelRotation,
                                scaleFactor,
                                props.theme.axis.xLabelStroke,
                                scaledFont
                            );
                        }

                        // Render rotated labels for expanded timestamp format
                        if (Math.abs(labelRotation) > 0 && timestampFormat === 'expanded') {
                            const dpr = window.devicePixelRatio || 1;
                            renderRotatedExpandedLabels(
                                u,
                                getRotatedLabels(),
                                labelRotation,
                                props.theme.axis.xLabelStroke,
                                parseAndScaleFont(props.theme.font, dpr)
                            );
                        }
                    },

                    draw: (u): void => {
                        if (destroyed) {
                            // eslint-disable-next-line no-param-reassign
                            u.root.dataset.uplotDrawn = 'destroyed';
                            return;
                        }
                        // Align the `containerEl` to the `uplot.over` element.
                        const rootRect = u.root.getBoundingClientRect();
                        const overRect = u.over.getBoundingClientRect();

                        function align(el: HTMLElement): void {
                            // eslint-disable-next-line no-param-reassign
                            el.style.top = `${(overRect.top - rootRect.top) / scaleFactor}px`;
                            // eslint-disable-next-line no-param-reassign
                            el.style.left = `${(overRect.left - rootRect.left) / scaleFactor}px`; // eslint-disable-next-line no-param-reassign
                            // eslint-disable-next-line no-param-reassign
                            el.style.width = `${overRect.width / scaleFactor}px`;
                            // eslint-disable-next-line no-param-reassign
                            el.style.height = `${overRect.height / scaleFactor}px`;
                        }

                        if (props.interactiveEl) {
                            align(props.interactiveEl);
                        }
                        // eslint-disable-next-line no-param-reassign
                        u.root.dataset.uplotDrawn = 'drawn';
                    },

                    destroy: (): void => {
                        destroyed = true;
                    },
                },
            },
        ];

        // Plugin to redraw overlay (type: 'line') series on top of stacked area fills.
        // uPlot draws series in index order; the draw hook re-paints overlay series last
        // using uPlot's internally cached paths and resolved stroke color (_paths, _stroke).
        const overlaySeriesIndices = series
            .map((s, i) => (s.key && props.seriesConfig?.[s.key]?.type === 'line' ? i : -1))
            .filter(i => i >= 0);
        if (overlaySeriesIndices.length > 0) {
            basePlugins.push({
                hooks: {
                    draw: (u): void => {
                        // Cast to record to access uPlot internal cached fields (_paths, _stroke, ctx, bbox)
                        const uInternal = u as unknown as Record<string, unknown>;
                        const ctx = uInternal.ctx as CanvasRenderingContext2D;
                        const bbox = uInternal.bbox as {
                            left: number;
                            top: number;
                            width: number;
                            height: number;
                        };
                        const internalSeries = uInternal.series as Record<string, unknown>[];
                        overlaySeriesIndices.forEach(si => {
                            const s = u.series[si];
                            /* eslint-disable no-underscore-dangle */
                            const cachedPaths = internalSeries[si]._paths as
                                | { stroke: Path2D | null }
                                | null
                                | undefined;
                            const cachedStroke = internalSeries[si]._stroke as string | null | undefined;
                            /* eslint-enable no-underscore-dangle */
                            if (!s.show || !cachedPaths?.stroke || !cachedStroke) return;
                            ctx.save();
                            ctx.beginPath();
                            ctx.rect(bbox.left, bbox.top, bbox.width, bbox.height);
                            ctx.clip();
                            ctx.strokeStyle = cachedStroke;
                            ctx.lineWidth = (s.width ?? 1) * (window.devicePixelRatio || 1);
                            ctx.setLineDash(s.dash ?? []);
                            ctx.stroke(cachedPaths.stroke);
                            ctx.restore();
                        });
                    },
                },
            });
        }

        // Add data labels plugin if dataValuesDisplay is enabled
        if (props.dataValuesDisplay && props.dataValuesDisplay !== 'off') {
            basePlugins.push(
                createDataLabelsPlugin({
                    mode: props.dataValuesDisplay,
                    theme: props.theme,
                })
            );
        }

        // Custom cursor points plugin to handle zero values and last index correctly
        // Standard mode: show dot for closest series only
        // SeriesCompare mode: show dots for all series with data
        {
            // Create an overlay canvas for cursor points
            let overlayCanvas: HTMLCanvasElement | null = null;
            let overlayCtx: CanvasRenderingContext2D | null = null;
            // Padding to prevent hover marker (20px outer halo) from being clipped at edges
            const POINT_PADDING = HOVER_MARKER_OUTER_RADIUS_PX;
            // Track CSS dimensions for clearing in the DPR-transformed coordinate space
            let overlayCssWidth = 0;
            let overlayCssHeight = 0;

            const clearOverlay = (): void => {
                if (!overlayCtx || !overlayCanvas) return;
                // Use CSS dimensions that match the DPR-transformed drawing coordinate space
                overlayCtx.clearRect(
                    0,
                    0,
                    overlayCssWidth || overlayCanvas.width,
                    overlayCssHeight || overlayCanvas.height
                );
            };

            basePlugins.push({
                hooks: {
                    init: (u): void => {
                        clearOverlay();
                        // Remove any orphaned overlay canvases from previous instances.
                        // When uPlot options change and the instance is recreated, the old
                        // destroy hook may not fire, leaving stale canvases with old dots.
                        u.root
                            .querySelectorAll('canvas[data-overlay="cursor-points"]')
                            .forEach(el => el.remove());

                        // Create overlay canvas for cursor points
                        // Append to u.root (not u.over) to avoid overflow clipping
                        overlayCanvas = document.createElement('canvas');
                        overlayCanvas.setAttribute('data-overlay', 'cursor-points');
                        overlayCanvas.style.position = 'absolute';
                        overlayCanvas.style.pointerEvents = 'none';
                        overlayCanvas.style.zIndex = '100';
                        u.root.appendChild(overlayCanvas);
                        overlayCtx = overlayCanvas.getContext('2d');

                        // Ensure u.root doesn't clip the overlay canvas padding
                        // (the canvas extends POINT_PADDING beyond the plot area for edge dots)
                        Object.assign(u.root.style, { overflow: 'visible' });

                        // Safety net: clear overlay when mouse leaves the chart area.
                        // Using u.root (outermost uPlot element) for reliable event coverage.
                        // This catches scenarios where setCursor may not fire with an
                        // off-screen position (e.g., crosshair sync race conditions).
                        u.root.addEventListener('mouseleave', clearOverlay);
                    },
                    setSize: (u): void => {
                        clearOverlay();

                        props.onSetSize?.();
                        if (overlayCanvas && overlayCtx) {
                            // Position canvas to match u.over but with padding for edge dots
                            // getBoundingClientRect() returns screen pixels; CSS positioning
                            // needs pre-transform values, so divide by scaleFactor
                            const overRect = u.over.getBoundingClientRect();
                            const rootRect = u.root.getBoundingClientRect();
                            const offsetLeft = (overRect.left - rootRect.left) / scaleFactor - POINT_PADDING;
                            const offsetTop = (overRect.top - rootRect.top) / scaleFactor - POINT_PADDING;

                            const dpr = window.devicePixelRatio || 1;
                            const canvasWidth = overRect.width / scaleFactor + POINT_PADDING * 2;
                            const canvasHeight = overRect.height / scaleFactor + POINT_PADDING * 2;

                            overlayCanvas.width = canvasWidth * dpr;
                            overlayCanvas.height = canvasHeight * dpr;
                            overlayCanvas.style.width = `${canvasWidth}px`;
                            overlayCanvas.style.height = `${canvasHeight}px`;
                            overlayCanvas.style.left = `${offsetLeft}px`;
                            overlayCanvas.style.top = `${offsetTop}px`;

                            // Track CSS dimensions for clearing in the DPR-transformed space
                            overlayCssWidth = canvasWidth;
                            overlayCssHeight = canvasHeight;

                            // Reset transform and scale for new size
                            overlayCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
                        }
                    },
                    setCursor: (u): void => {
                        if (!overlayCtx || !overlayCanvas) return;

                        // Clear previous points
                        clearOverlay();

                        const idx = u.cursor?.idx;
                        const cursorLeft = u.cursor?.left;
                        const cursorTop = u.cursor?.top;

                        // Clear and return if cursor is outside chart
                        // Check both idx and cursor position to handle mouseleave case
                        // u.bbox dimensions are in canvas physical pixels; convert to CSS pixels
                        // for comparison with cursor coordinates (which are CSS px)
                        const pxRatio = window.devicePixelRatio || 1;
                        if (
                            idx == null ||
                            idx < 0 ||
                            cursorLeft == null ||
                            cursorTop == null ||
                            cursorLeft < 0 ||
                            cursorTop < 0 ||
                            cursorLeft > u.bbox.width / pxRatio ||
                            cursorTop > u.bbox.height / pxRatio
                        ) {
                            return;
                        }

                        const isSeriesCompare = maxSeriesFocus === 0;

                        if (isSeriesCompare) {
                            // SeriesCompare mode: only draw dots when hovering chart, not just legend
                            // Check if any series is focused (legend hover without chart hover)
                            const hasFocusedSeries = u.series.some(
                                (s, i) => i > 0 && (s as uPlot.Series & { focus?: boolean }).focus
                            );
                            if (!hasFocusedSeries) {
                                // No focused series, draw dots for ALL series with data
                                drawAllSeriesPoints(u, overlayCtx, idx, POINT_PADDING);
                            }
                        } else {
                            // Standard mode: draw dot for CLOSEST series only
                            drawClosestSeriesPoint(u, overlayCtx, idx, cursorTop, POINT_PADDING);
                        }
                    },
                    destroy: (u): void => {
                        u.root.removeEventListener('mouseleave', clearOverlay);
                        clearOverlay();
                        overlayCanvas?.parentNode?.removeChild(overlayCanvas);
                        overlayCanvas = null;
                        overlayCtx = null;
                    },
                },
            });
        }

        // Determine focus configuration based on mode
        const getFocusConfig = () => {
            if (maxSeriesFocus === 0) {
                // seriesCompare: disable focus dimming entirely
                return { prox: -1 };
            }
            if (series.length > maxSeriesFocus + 1) {
                return { show: false, prox: 0 };
            }
            return { prox: 1000 };
        };

        const getSeriesFocusConfig = () => {
            if (maxSeriesFocus === 0) {
                return { alpha: 1 };
            }

            return { alpha: INACTIVE_SERIES_ALPHA };
        };

        return {
            width: 0,
            height: 0,
            series,
            bands,
            plugins: Array.isArray(props.plugins) ? basePlugins.concat(props.plugins) : basePlugins,
            cursor: {
                x: false,
                y: false,
                // Focus controls series highlighting (dimming non-focused series)
                // Standard mode: dim non-closest series
                // SeriesCompare mode (maxSeriesFocus=0): no dimming, all series highlighted
                focus: getFocusConfig(),
                hover: {
                    prox: shouldLimitHoverDistance ? 50 : 1000,
                },
                drag: {
                    setScale: false,
                    x: false,
                    y: false,
                },
                // Disable native cursor points - we use custom overlay plugin instead
                points: {
                    show: false,
                },
            },
            focus: getSeriesFocusConfig(),
            pxAlign: false,
            pxRatio: window.devicePixelRatio || 1,
            scales,
            axes,
            legend: { show: false },
            padding:
                timestampFormat === 'expanded'
                    ? calculateExpandedPadding(props.scaleConfig, props.seriesConfig)
                    : undefined, // For multi-line format x-axis labels; adjust left/right padding based on Y-axis configuration
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        series,
        bands,
        props.onReady,
        props.onSetCursor,
        props.onSetSeries,
        props.theme.axis.axisStroke,
        props.interactiveEl,
        maxSeriesFocus,
        shouldLimitHoverDistance,
        scales,
        axes,
        timestampFormat,
        scaleFactor,
        dprVersion, // Force recreation when DPR changes
        props.scaleConfig, // Add scaleConfig to dependencies for padding recalculation
    ]);

    const { height, width } = props;

    const options = useMemo(
        () => ({
            ...opts,
            width,
            height,
            series,
        }),
        [height, opts, series, width]
    );

    return <UplotReact key={`uplot-${dprVersion}-${scaleFactor}`} options={options} data={alignedData} />;
};

/**
 * Should only be called once per chart instatiation
 */
export const uPlotPlotRendererFactory: PlotRendererFactory = options => {
    let uPlotRef: uPlot | undefined;
    const originalScales: Record<string, { min: number; max: number }> = {};

    function getUplotColorAsString(
        fillOrStroke: uPlot.Series.Fill | uPlot.Series.Stroke | undefined,
        i: number
    ): string | undefined {
        if (!uPlotRef || !fillOrStroke) {
            return undefined;
        }

        if (isFunction(fillOrStroke)) {
            const fillResponse = fillOrStroke(uPlotRef, i);
            if (fillResponse === null || typeof fillResponse !== 'string') {
                return undefined;
            }
            return fillResponse;
        }

        return typeof fillOrStroke === 'string' ? fillOrStroke : undefined;
    }

    const utils = (): PlotUtilities | undefined =>
        !uPlotRef
            ? undefined
            : {
                  dimensions: {
                      root: uPlotRef.root.getBoundingClientRect(),
                      over: uPlotRef.over.getBoundingClientRect(),
                  },
                  valToPos: (value, scaleKey): number => uPlotRef?.valToPos(value, scaleKey) || 0,
                  posToVal: (value, scaleKey): number => uPlotRef?.posToVal(value, scaleKey) || 0,
                  setCursor: (cursor, fire = false): void => {
                      uPlotRef?.setCursor(cursor, fire);
                  },
                  setFocusedSeries: (field: string | null): void => {
                      if (field === null) {
                          uPlotRef?.setSeries(null, { focus: true }, false);
                      } else {
                          const seriesIdx = (uPlotRef?.series as SeriesWithKey[]).findIndex(
                              s => s.key === field
                          );
                          uPlotRef?.setSeries(seriesIdx, { focus: true }, false);
                      }
                  },
                  setScale: (scaleKey: string, min: number, max: number): void => {
                      if (uPlotRef) {
                          // Store original scale on first zoom if not already stored
                          if (!originalScales[scaleKey] && uPlotRef.scales?.[scaleKey]) {
                              const scale = uPlotRef.scales[scaleKey];
                              originalScales[scaleKey] = {
                                  min: scale.min ?? 0,
                                  max: scale.max ?? 0,
                              };
                          }
                          uPlotRef.setScale?.(scaleKey, { min, max });
                      }
                  },
                  resetScale: (scaleKey: string): void => {
                      if (uPlotRef && originalScales[scaleKey]) {
                          // Restore the original scale range
                          const { min, max } = originalScales[scaleKey];
                          uPlotRef.setScale?.(scaleKey, { min, max });
                      }
                  },
                  panLeft: (scaleKey: string): boolean => {
                      if (!uPlotRef?.scales?.[scaleKey] || !originalScales[scaleKey]) {
                          return false;
                      }

                      const currentScale = uPlotRef.scales[scaleKey];
                      const currentMin = currentScale.min ?? 0;
                      const currentMax = currentScale.max ?? 0;
                      const originalMin = originalScales[scaleKey].min;

                      // Calculate pan distance (10% of visible range)
                      const range = currentMax - currentMin;
                      const panDistance = range * 0.1;

                      // Calculate new bounds
                      const newMin = Math.max(originalMin, currentMin - panDistance);
                      const newMax = newMin + range;

                      // Apply the pan
                      uPlotRef.setScale?.(scaleKey, { min: newMin, max: newMax });

                      // Return true if we can still pan further left
                      return newMin > originalMin;
                  },
                  panRight: (scaleKey: string): boolean => {
                      if (!uPlotRef?.scales?.[scaleKey] || !originalScales[scaleKey]) {
                          return false;
                      }

                      const currentScale = uPlotRef.scales[scaleKey];
                      const currentMin = currentScale.min ?? 0;
                      const currentMax = currentScale.max ?? 0;
                      const originalMax = originalScales[scaleKey].max;

                      // Calculate pan distance (10% of visible range)
                      const range = currentMax - currentMin;
                      const panDistance = range * 0.1;

                      // Calculate new bounds
                      const newMax = Math.min(originalMax, currentMax + panDistance);
                      const newMin = newMax - range;

                      // Apply the pan
                      uPlotRef.setScale?.(scaleKey, { min: newMin, max: newMax });

                      // Return true if we can still pan further right
                      return newMax < originalMax;
                  },
                  getScaleRange: (scaleKey: string): { min: number; max: number } | undefined => {
                      if (!uPlotRef?.scales?.[scaleKey]) {
                          return undefined;
                      }
                      const scale = uPlotRef.scales[scaleKey];
                      return {
                          min: scale.min ?? 0,
                          max: scale.max ?? 0,
                      };
                  },
                  subscribeToScale: (scaleKey: string, fn: () => void): (() => void) => {
                      if (!scaleListeners[scaleKey]) {
                          scaleListeners[scaleKey] = [];
                      }
                      scaleListeners[scaleKey].push(fn);
                      return (): void => {
                          scaleListeners[scaleKey] = (scaleListeners[scaleKey] || []).filter(
                              listener => listener !== fn
                          );
                      };
                  },
                  allSeries: (uPlotRef.series as SeriesWithKey[]).map((s, i) => ({
                      field: s.key,
                      label: s.label,
                      fill: getUplotColorAsString(s.fill, i),
                      stroke: getUplotColorAsString(s.stroke, i),
                      show: s.show || false,
                  })),
                  el: uPlotRef.root,
              };

    const scaleListeners: Record<string, Array<() => void>> = {};
    const fireScaleListeners = (_u: uPlot, scaleKey: string): void => {
        scaleListeners[scaleKey]?.forEach(fn => fn());
    };

    const onSetCursorListeners: OnSetCursorFn[] = [];
    const fireSetCursorListeners = (u: uPlot): void => {
        onSetCursorListeners.forEach(fn => {
            if (u.cursor?.idx == null) {
                fn(null);
            } else {
                // Update the top and left of the cursor; keep index the same
                fn({
                    top: isNumber(u.cursor.top) ? u.cursor.top : -10,
                    left: isNumber(u.cursor.left) ? u.cursor.left : -10,
                    index: isNumber(u.cursor.idx) ? u.cursor.idx : -10,
                });
            }
        });
    };

    const onFocusSeriesListeners: OnFocusSeriesFn[] = [];
    const fireFocusSeriesListeners = (_u: uPlot, seriesIndex: number | null): void => {
        onFocusSeriesListeners.forEach(fn => {
            fn(seriesIndex);
        });
    };

    function onReady(u: uPlot): void {
        uPlotRef = u;
        options.onReady?.(utils, uPlotRef);
    }

    return {
        utils,
        renderer: {
            onSetCursor: (fn): void => {
                onSetCursorListeners.push(fn);
            },
            onFocusSeries: (fn): void => {
                onFocusSeriesListeners.push(fn);
            },
            component: (props): ReturnType<React.FC> => (
                <UPlotRenderComponent
                    {...props}
                    onReady={onReady}
                    onSetCursor={fireSetCursorListeners}
                    onSetSeries={fireFocusSeriesListeners}
                    onSetScale={fireScaleListeners}
                    onSetSize={options.onSetSize}
                />
            ),
        },
    };
};

export const useUplotPlotRendererFactory = (
    onReady?: (PlotUtilitiesFn: PlotUtilitiesFn, uplotRef?: uPlot) => void
): ReturnType<typeof uPlotPlotRendererFactory> => {
    const [, triggerRender] = React.useReducer(count => count + 1, 0);
    const forceUpdate = React.useCallback(() => triggerRender(), []);

    // Create reference the first time this hook is used, then always just return that reference.
    // This prevents the factory from being called twice.
    const [ref] = useState<ReturnType<typeof uPlotPlotRendererFactory>>(() =>
        uPlotPlotRendererFactory({
            onReady: (utils, uplotRef) => {
                onReady?.(utils, uplotRef);
                forceUpdate();
            },
            // SCP-80914: re-render after every uPlot layout pass so all annotation
            // portals call valToPos with up-to-date x-scale coordinates.
            onSetSize: forceUpdate,
        })
    );

    return ref;
};
